import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const ScrollToTop = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevPathRef = useRef(location.pathname);
  const isHomePage = location.pathname === '/';
  const HOME_SCROLL_KEY = 'homeScrollPosition';

  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;
    const isNowOnHome = currentPath === '/';

    // Save scroll position when leaving home page (before navigation completes)
    if (prevPath === '/' && !isNowOnHome) {
      sessionStorage.setItem(HOME_SCROLL_KEY, window.scrollY.toString());
    }

    // Handle scroll restoration or scroll to top
    if (isNowOnHome && navigationType === 'POP') {
      // User clicked back button to return to home - restore saved scroll position
      const savedPosition = sessionStorage.getItem(HOME_SCROLL_KEY);
      if (savedPosition) {
        const position = parseInt(savedPosition, 10);
        // Double requestAnimationFrame to ensure DOM is ready and navbar transitions complete
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo({
              top: position,
              behavior: 'instant',
            });
          });
        });
      } else {
        // No saved position, scroll to top
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    } else {
      // Normal navigation (PUSH/REPLACE) - scroll to top
      // Small delay to allow navbar transitions
      const timer = setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'instant',
        });
      }, 0);

      return () => clearTimeout(timer);
    }

    // Update previous path for next navigation
    prevPathRef.current = currentPath;
  }, [location.pathname, navigationType]);

  // Continuously save scroll position when on home page
  useEffect(() => {
    if (!isHomePage) return;

    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        sessionStorage.setItem(HOME_SCROLL_KEY, window.scrollY.toString());
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
      // Save final position when leaving home
      sessionStorage.setItem(HOME_SCROLL_KEY, window.scrollY.toString());
    };
  }, [isHomePage]);

  return null;
};

export default ScrollToTop;
